import { useState, useEffect } from "react";
import ShimmerLoader from "../../components/ShimmerLoader";
import { useDispatch, useSelector } from "react-redux";
import { fetchbookingcalendar } from "../../features/flight/flightSlice";
import { selectCurrentUser } from "../../features/auth/authSlice";
import Moment from "moment";
import { format, parseISO } from "date-fns";
import { Funnel } from "lucide-react";
import CurFt from "../../components/CurFt";
import CursorPagination from "../../components/CursorPagination";
import useDocumentTitle from "../../hooks/useDocumentTitle";

export default function BookingCalendar() {
  useDocumentTitle("Booking Calendar");
  const dispatch = useDispatch();
  const { bookingcalendarStatus, bookingcalendar } = useSelector((state) => state.flights);
  const perPage = 25;
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [BOOKINGSTATUS, SetBOOKINGSTATUS] = useState({});
  const [BookingHistory, SetBookingHistory] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const user = useSelector(selectCurrentUser);
  const [FilterData, setFilterData] = useState({
    filterType: "Flight",
    bookingID: "",
    PNRNumber: "",
    BookingStatus: [1, 9, 20, 26, 27, 28, 29, 30, 14],
    supplierType: "All",
    JourneyType: "All",
    Type: "All",
    FirstName: "",
    FromDate: null,
    ToDate: null,
    TravelFromDate: null,
    TravelToDate: null,
    validate: true,
  });
  const fetchBookingHistory = async (cursor = null) => {
    dispatch(fetchbookingcalendar({ FilterData, perPage, cursor }));
  };
  useEffect(() => {
    fetchBookingHistory();
  }, [FilterData]);

  useEffect(() => {
    const data = bookingcalendar?.Booking;
    SetBookingHistory(data?.data || []);
    SetBOOKINGSTATUS(bookingcalendar?.BOOKINGSTATUS || {});
    setNextCursor(data?.next_cursor);
    setPrevCursor(data?.prev_cursor);
  }, [bookingcalendarStatus, bookingcalendar]);

  const handleNextPage = () => {
    if (nextCursor) {
      fetchBookingHistory(nextCursor);
    }
  };
  const SupplierType = [
    { id: 1, name: "TRIPJACK" },
    { id: 2, name: "TBO" },
    { id: 3, name: "SF/GF" },
    { id: 4, name: "BHASIN" },
    { id: 5, name: "OFFLINE" },
  ];
  const handlePrevPage = () => {
    if (prevCursor) {
      fetchBookingHistory(prevCursor);
    }
  };
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (field, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  return (
    <div className="bg-gray-100 text-gray-800 relative min-h-screen">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 space-y-6">
        <div className="bg-white shadow-md rounded-lg p-4 mt-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Recent Booking</h3>
            <span className="font-semibold text-blue-500 flex items-center gap-1">
              <div className="relative inline-block text-left">
                <button
                  onClick={toggleFilters}
                  className={`px-5 py-2 rounded font-medium shadow transition flex items-center gap-2 ${showFilters ? "bg-blue-600 text-white" : "bg-blue-500 hover:bg-blue-600 text-white"
                    }`}
                >
                  <Funnel size={18} />
                  Filters
                </button>
              </div>
            </span>
          </div>
        </div>
        {showFilters && (
          <div className="bg-white shadow-md rounded-lg p-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* First Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking ID</label>
                  <input
                    type="text"
                    value={FilterData.bookingID}
                    onChange={(e) => handleFilterChange("bookingID", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Booking ID"
                  />
                </div>
              </div>

              {/* Second Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">PNR Number</label>
                  <input
                    type="text"
                    value={FilterData.PNRNumber}
                    onChange={(e) => handleFilterChange("PNRNumber", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="PNR Number"
                  />
                </div>
              </div>

              {/* Third Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Journey Type</label>
                  <select
                    value={FilterData.JourneyType}
                    onChange={(e) => handleFilterChange("JourneyType", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="All">All</option>
                    <option value="0">Domestic</option>
                    <option value="1">International</option>
                  </select>
                </div>
              </div>

              {/* Fourth Column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Supplier</label>
                  <select
                    value={FilterData.supplierType}
                    onChange={(e) => handleFilterChange("supplierType", e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="All">All</option>
                    {SupplierType.map((item, index) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Fifth Column - Empty for spacing */}
              <div className="space-y-4"></div>

              {/* Second Row - Next 5 inputs */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking From Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={FilterData.FromDate ? new Date(FilterData.FromDate).toISOString().split("T")[0] : ""}
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("FromDate", formattedDate);
                      }}
                      max={new Date().toISOString().split("T")[0]}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.FromDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("FromDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Booking To Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={FilterData.ToDate ? new Date(FilterData.ToDate).toISOString().split("T")[0] : ""}
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("ToDate", formattedDate);
                      }}
                      // max={new Date().toISOString().split("T")[0]}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.ToDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("ToDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Travel From Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={
                        FilterData.TravelFromDate ? new Date(FilterData.TravelFromDate).toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("TravelFromDate", formattedDate);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.TravelFromDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("TravelFromDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Travel To Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={
                        FilterData.TravelToDate ? new Date(FilterData.TravelToDate).toISOString().split("T")[0] : ""
                      }
                      onChange={(e) => {
                        const date = e.target.value;
                        const formattedDate = date ? new Date(date + "T18:30:00.000Z").toISOString() : null;
                        handleFilterChange("TravelToDate", formattedDate);
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm pr-10 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {FilterData.TravelToDate && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("TravelToDate", null)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Close Button in the last column */}
              <div className="space-y-4">
                <div className="pt-6">
                  <button
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Close Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow-md rounded-lg overflow-hidden p-2">
          <div className="overflow-x-auto">
            <table className="min-w-full border  text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 border text-left">No.</th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Trx Date
                  </th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Travel Date/Time
                  </th>
                  <th className="px-3 py-2 border text-left">Booking ID/PNR</th>
                  <th className="px-3 py-2 border text-left">Summary</th>

                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Sales Price
                    {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                      <>
                        <br />
                        My Cost
                      </>
                    )}
                  </th>
                  {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                    <th className="px-3 py-2 border text-left" nowrap="true">
                      Earning
                    </th>
                  )}
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    Passenger Name
                  </th>
                  <th className="px-3 py-2 border text-left" nowrap="true">
                    {" "}
                    Status
                  </th>
                  <th className="px-3 py-2 border text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookingcalendarStatus === "loading" ? (
                  <>
                    <tr className="border-b">
                      <td className="px-3 py-2 border" colSpan={12}>
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                        <ShimmerLoader />
                      </td>
                    </tr>
                  </>
                ) : (
                  <>
                    {BookingHistory.length > 0 ? (
                      BookingHistory.map((item, key) => (
                        <tr className="border-b" key={key}>
                          <td className="px-3 py-2 border">{key + 1}</td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {format(parseISO(item.created_at), "dd MMM, yyyy")}
                            <br />
                            {Moment(item.created_at).format("h:mm A")}
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {Moment(item.FromUTCTime).format("ddd Do MMM")}
                            <br />
                            {Moment(item.FromUTCTime).format("h:mm A")}
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            <a
                              target="_blank"
                              style={{ color: "blue", textDecoration: "underline" }}
                              href={`/flight/booking-confirmation/${item.BookingID}`}
                            >
                              {item.BookingID}
                            </a>
                            <br />
                            PNR - {[...new Set(item.PNRNumber.split(","))].join(", ")}
                          </td>
                          <td className="px-3 py-2 border">
                            {item.JourneyType === 2 ? (
                              <>{item.SoCode + "⇆" + item.DeCode + " " + item.FlightNumber}</>
                            ) : (
                              <>{item.SoCode + "-" + item.DeCode + " " + item.FlightNumber}</>
                            )}

                            <br />
                            {item.AirlineName}
                          </td>
                          <td className="px-3 py-2 border">
                            <CurFt
                              currency={item?.Currncy || "INR"}
                              price={
                                parseFloat(item.PubFare) +
                                parseFloat(item.BagPrice) +
                                parseFloat(item.MealPrice) +
                                parseFloat(item.SeatPrice) +
                                parseFloat(item.agent_markup) -
                                parseFloat(item.CouponDiscount)
                              }
                            />
                            {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                              <>
                                <br />
                                <CurFt
                                  currency={item?.Currncy || "INR"}
                                  price={
                                    parseFloat(item.PubFare) +
                                    parseFloat(item.BagPrice) +
                                    parseFloat(item.MealPrice) +
                                    parseFloat(item.SeatPrice) -
                                    parseFloat(item.CouponDiscount)
                                  }
                                />
                              </>
                            )}
                          </td>
                          {user.UserData.user.UserType != 6 && user.UserData.user.UserType != 7 && (
                            <td className="px-3 py-2 border">
                              <CurFt currency={item?.Currncy || "INR"} price={parseFloat(item.agent_markup)} />
                            </td>
                          )}
                          <td className="px-3 py-2 border" nowrap="true">
                            {item.customerName} <br />
                            Total Pax:{item.totalPax}
                          </td>
                          <td className="px-3 py-2 border" nowrap="true">
                            {item.BookingStatus == 1 || item.BookingStatus == 29 ? (
                              <span style={{ color: "green" }}>{BOOKINGSTATUS[item.BookingStatus]}</span>
                            ) : (
                              <span style={{ color: "red" }}>{BOOKINGSTATUS[item.BookingStatus]}</span>
                            )}

                            <br />
                            {(item.BookingStatus == 1 || item.BookingStatus == 29) &&
                              item.oldBookingId != "" &&
                              item.oldBookingId != "0" &&
                              item.oldBookingId?.toString().length > 2
                              ? "Ref. - " + item.oldBookingId
                              : ""}

                            {item.supplierType === 9 ? 'GROUP TICKETS' : ''}
                          </td>
                          <td className="text-right px-3 py-2 border">
                            <a
                              target="_blank"
                              style={{ color: "blue", textDecoration: "underline" }}
                              href={`/flight/booking-confirmation/${item.BookingID}`}
                            >
                              View
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <tr className="border-b">
                          <td colSpan={12} className="px-3 py-2 border">
                            No record found
                          </td>
                        </tr>
                      </>
                    )}
                  </>
                )}
              </tbody>
            </table>
            <CursorPagination
              nextCursor={nextCursor}
              prevCursor={prevCursor}
              onNext={handleNextPage}
              onPrev={handlePrevPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
